import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { treeDndStore } from '@/components/tree-dnd/tree-dnd-store';
import { INITIAL_TREE } from '@/components/tree-dnd/tree-dnd-utils';
import { useI18nContext } from '@/i18n/i18n-react';

interface AudienceCreateModeSwitchProps {
  tenantSlug: string;
  audienceId?: string;
  mode: 'simplified' | 'advanced';
}

export function AudienceCreateModeSwitch({
  tenantSlug,
  audienceId,
  mode,
}: AudienceCreateModeSwitchProps) {
  const { LL } = useI18nContext();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isEditMode = Boolean(audienceId);

  const advancedPath = isEditMode
    ? `/${tenantSlug}/audiences/${audienceId}/edit-advanced`
    : `/${tenantSlug}/audiences/create-advanced`;
  const simplifiedPath = isEditMode
    ? `/${tenantSlug}/audiences/${audienceId}/edit`
    : `/${tenantSlug}/audiences/create`;

  if (mode === 'simplified') {
    return (
      <Button
        type="button"
        variant="outline"
        onClick={() => navigate(advancedPath)}
      >
        {LL.audiences.create.modeSwitch.toAdvanced()}
      </Button>
    );
  }

  const handleConfirmSwitch = () => {
    treeDndStore.getState().setTree(INITIAL_TREE);
    setConfirmOpen(false);
    navigate(simplifiedPath);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setConfirmOpen(true)}
      >
        {LL.audiences.create.modeSwitch.toSimplified()}
      </Button>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {LL.audiences.create.modeSwitch.confirmSwitchToSimplified.title()}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {LL.audiences.create.modeSwitch.confirmSwitchToSimplified.description()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {LL.audiences.create.modeSwitch.confirmSwitchToSimplified.cancel()}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSwitch}>
              {LL.audiences.create.modeSwitch.confirmSwitchToSimplified.confirm()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
